import React, { useContext, useState, useMemo, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
	Button,
	CardContent,
	Card,
	Typography,
	Drawer,
	Stack,
	TableContainer,
	Table,
	TableRow,
	TableHead,
	TableCell,
	TableBody,
	Dialog,
	DialogContent,
	IconButton,
	Toolbar,
	AppBar,
	Slide,
} from '@mui/material'
import { MonopolyContext } from '../../contexts/MonopolyContext'
import { ref, onValue } from 'firebase/database'
import AnimatedNumber from 'animated-number-react'
import Pay from './Drawers/Pay'
import Receive from './Drawers/Receive'
import { useSnackbar } from 'notistack'
import { ManageSearch, Close } from '@mui/icons-material'
import { red, green } from '@mui/material/colors'
import ReactTimeAgo from 'react-time-ago'
import { styled } from '@mui/material/styles'

const SlideUpTransition = React.forwardRef((props, ref) => {
	return <Slide direction='up' ref={ref} {...props} />
})

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	'&.action-pay > *': {
		color: red[200],
	},
	'&.action-receive > *': {
		color: green[200],
	},
}))

const Game = () => {
	const { name, setName, db, M } = useContext(MonopolyContext)

	const { enqueueSnackbar, closeSnackbar } = useSnackbar()

	const printedSnacks = useMemo(_ => ({}), [])

	const navigate = useNavigate()

	const [balance, setBalance] = useState(0),
		[joined, setJoined] = useState(false),
		[historyDialog, setHistoryDialog] = useState(false),
		[drawer, setDrawer] = useState(false),
		[users, setUsers] = useState([]),
		location = useLocation(),
		urlParts = location.pathname.split('/'),
		roomCode = urlParts[2],
		myUserId = localStorage.getItem('userId'),
		joinRoom = _ => {
			navigate('/?join=' + roomCode)
		}

	// if (!name) {
	// 	localStorage.setItem('lastRoomCode', roomCode)
	// 	const navigate = useNavigate()
	// 	navigate('/')
	// }

	const parseFirebase = useMemo(
		() => ss => {
			const data = ss.val(),
				currentUsers = []

			// parse users
			for (const userId in data.users) {
				let user = data.users[userId]
				// update my data
				if (userId === myUserId) {
					setJoined(true)
					setName(user.name)
					setBalance(user.balance ?? 0)
				}

				currentUsers.push({
					userId,
					...user,
				})
			}
			currentUsers.sort((a, b) => (b.balance ?? 0) - (a.balance ?? 0))
			setUsers(currentUsers)

			// show snacks
			const currentTime = new Date().valueOf(),
				showSnacksFor = 1000 * 60
			for (const historyIndex in data.history) {
				if (
					!printedSnacks[historyIndex] &&
					data.history[historyIndex].time > currentTime - showSnacksFor
				) {
					printedSnacks[historyIndex] = 1
					let text = false,
						type = 'success',
						mainUser,
						to,
						historyItem = data.history[historyIndex]
					switch (historyItem.action) {
						case 'sentToEveryone':
							type = 'success'
							mainUser = currentUsers.find(
								user => user.userId === historyItem.user
							)
							text = `${mainUser.name} paid ${historyItem.amount} M to everyone`
							break
						case 'sentToBank':
							type = 'error'
							mainUser = currentUsers.find(
								user => user.userId === historyItem.user
							)
							text = `${mainUser.name} paid ${historyItem.amount} M in fees`
							break
						case 'sentToUser':
							type = 'info'
							console.log(historyItem)
							mainUser = currentUsers.find(
								user => user.userId === historyItem.user
							)
							if (historyItem.to.substr(0, 5) === 'user:') {
								historyItem.to = historyItem.to.substr(5)
							}
							to = currentUsers.find(user => user.userId === historyItem.to)
							text = `${mainUser.name} paid ${historyItem.amount} M to ${to.name}`
							break
						case 'received':
							type = 'warning'
							mainUser = currentUsers.find(
								user => user.userId === historyItem.user
							)
							text = `${mainUser.name} received ${historyItem.amount} M`
							break
						case 'receivedByGo1':
						case 'receivedByGo2':
							type = 'warning'
							mainUser = currentUsers.find(
								user => user.userId === historyItem.user
							)
							text = `${mainUser.name} received ${historyItem.amount} M by passing go`
							break
						default:
							throw 'Unknown action: ' + historyItem.action
					}
					if (text) {
						console.log('ENQUEUE')
						const key = enqueueSnackbar(text, {
							variant: type,
							action: snackbarId => (
								<IconButton
									size='small'
									onClick={() => closeSnackbar(snackbarId)}
									sx={{ color: 'inherit' }}>
									<Close fontSize='small' />
								</IconButton>
							),
							onClick: (event, snackbarId) => {
								closeSnackbar(snackbarId)
							},
						})
					}
				}
			}
		},
		[
			myUserId,
			setName,
			setBalance,
			setUsers,
			setJoined,
			printedSnacks,
			enqueueSnackbar,
			closeSnackbar,
		]
	)

	const firebaseValueListenerRef = useRef(null)
	useEffect(() => {
		const roomRef = ref(db, `lobbies/${roomCode}`)
		const unsubscribe = onValue(roomRef, parseFirebase)
		firebaseValueListenerRef.current = unsubscribe

		return () => {
			if (firebaseValueListenerRef.current) {
				unsubscribe()
				firebaseValueListenerRef.current = null
			}
		}
	}, [db, roomCode, parseFirebase])

	const drawerProps = useMemo(
		_ => ({
			users,
			myUserId,
			roomCode,
			setDrawer,
		}),
		[users, myUserId, roomCode, setDrawer]
	)

	return (
		<>
			<Card>
				<CardContent>
					{joined && (
						<>
							<Typography color='#fff' variant='h4' component='h2'>
								{name}
							</Typography>

							<Typography color='#fff' variant='h3' component='h3'>
								<AnimatedNumber
									value={balance}
									formatValue={value => value.toFixed()}
									duration={200}
								/>
								<M />
							</Typography>
							<Stack spacing={2} direction='row' justifyContent='center' mt={2}>
								<Button
									variant='contained'
									color='red'
									onClick={_ => setDrawer('pay')}>
									Pay <M />
								</Button>
								<Button
									variant='contained'
									color='green'
									onClick={_ => setDrawer('receive')}>
									Receive <M />
								</Button>
							</Stack>
						</>
					)}
					<TableContainer>
						<Table
							sx={{ minWidth: 650 }}
							size='small'
							aria-label='a dense table'>
							<TableHead>
								<TableRow>
									<TableCell>User</TableCell>
									<TableCell align='right'>Balance</TableCell>
									<TableCell align='right'>Action</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{users.map((user, index) => (
									<TableRow key={index}>
										<TableCell component='th' scope='row'>
											{user.name}
										</TableCell>
										<TableCell align='right'>
											{user.balance ?? 0} <M />
										</TableCell>
										<TableCell align='right'>
											<Button
												size='small'
												onClick={_ => setHistoryDialog(user)}>
												<ManageSearch fontSize='small' />
											</Button>
										</TableCell>
									</TableRow>
								))}
								{!joined && location.search.includes('join') ? (
									<TableRow>
										<TableCell colSpan={3}>
											<Button
												variant='contained'
												color='primary'
												onClick={_ => joinRoom()}>
												Join Room
											</Button>
										</TableCell>
									</TableRow>
								) : null}
							</TableBody>
						</Table>
					</TableContainer>
				</CardContent>
			</Card>

			<Dialog
				open={!!historyDialog}
				fullScreen
				scroll='paper'
				TransitionComponent={SlideUpTransition}
				aria-labelledby='scroll-dialog-title'
				aria-describedby='scroll-dialog-description'>
				<AppBar sx={{ position: 'relative' }}>
					<Toolbar>
						<Typography sx={{ ml: 2, flex: 1 }} variant='h6' component='div'>
							{historyDialog && `Transaction History of ${historyDialog.name}`}
						</Typography>
						<IconButton
							edge='end'
							color='inherit'
							onClick={_ => setHistoryDialog(false)}
							aria-label='close'>
							<Close />
						</IconButton>
					</Toolbar>
				</AppBar>
				<DialogContent>
					<TableContainer>
						<Table
							sx={{ minWidth: 650 }}
							size='small'
							aria-label='a dense table'>
							<TableHead>
								<TableRow>
									<TableCell>When</TableCell>
									<TableCell>Action</TableCell>
									<TableCell align='right'>Amount</TableCell>
									<TableCell align='right'>From/To</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{historyDialog &&
									historyDialog.history &&
									historyDialog.history.map((historyItem, index) => {
										let otherUserId = historyItem.from || historyItem.to,
											otherUserName =
												otherUserId === 'bank'
													? 'Bank'
													: users.find(user => user.userId === otherUserId).name

										return (
											<StyledTableRow
												key={index}
												className={`action-${historyItem.action}`}>
												<TableCell component='th' scope='row'>
													<ReactTimeAgo
														date={historyItem.time}
														locale='en-US'
													/>
												</TableCell>
												<TableCell>{historyItem.action}</TableCell>
												<TableCell align='right'>
													{historyItem.amount} <M />
												</TableCell>
												<TableCell align='right'>
													{(historyItem.from && 'from ') ||
														(historyItem.to && 'to ')}
													{otherUserName}
												</TableCell>
											</StyledTableRow>
										)
									})}
							</TableBody>
						</Table>
					</TableContainer>
				</DialogContent>
			</Dialog>

			<Drawer anchor='bottom' open={!!drawer} onClose={_ => setDrawer(false)}>
				{(drawer === 'pay' && <Pay {...drawerProps} />) ||
					(drawer === 'receive' && <Receive {...drawerProps} />)}
			</Drawer>
		</>
	)
}

export default Game
